import React, { useState } from 'react';
import PropTypes from 'prop-types';
import FirmSection from 'components/FirmSection';

import FirmAboutAwardsSection from 'scenes/FirmAbout/components/FirmAboutAwardsSection';
import CompanyLogo from 'components/CompanyLogo';
import PhotoGallery from 'components/PhotoGallery';
import AddReviewButton from 'components/AddReviewButton';
import FirmAboutDescriptionSection from 'scenes/FirmAbout/components/FirmAboutDescriptionSection';
import FormDialog from 'scenes/Dialog/components/FormDialog';
import FirmAboutEditDescriptionBlockForm from 'scenes/Form/components/FirmAboutEditDescriptionBlockForm';
import FirmAboutEditAwardsForm from 'scenes/Form/components/FirmAboutEditAwardsForm';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import FirmAboutEditPhotos from 'scenes/FirmAbout/components/FirmAboutEditPhotos';
import { POPUP_STYLES } from 'constants/popup-styles';
import { ROUTE } from 'constants/route';

import './styles.css';

const FirmTabAbout = props => {
  const {
    isPhotosSortable,
    firm: { FirmName, Logo, FirmID },
    descriptionBlock = {
      FirmInfoBlockID: null,
      Title: 'Who We Are',
      Content: '',
    },
    awardsBlock = {
      FirmInfoBlockID: null,
      Title: 'Awards & Accolades',
      Content: '',
    },
    photosBlock = [],
    descriptionSubBlocks = [],
    onPhotoGallerySorting,
    onPhotoGalleryChangeSortingMode,
    onPhotoDelete,
    onPhotoUpload,
    onDescriptionBlockSave,
    onDescriptionBlock,
    onDescriptionSubBlockAdd,
    onDescriptionSubBlockUpdate,
    onDescriptionSubBlockDelete,
    onAwardsBlockSave,
    onAwardsBlockDelete,
    editMode,
  } = props;

  const [showAllPhotos, toggleAllPhotos] = useState(false);

  const viewMorePhotos = () => {
    toggleAllPhotos(!showAllPhotos);
  };

  const editDescriptionBlock = () => {
    FormDialog({
      title: '',
      modeless: false,
      compact: true,
      popupStyles: { ...POPUP_STYLES.formPopupStyles, maxWidth: '540px' },
      popupInnerStyles: { ...POPUP_STYLES.formInnerStyles, marginTop: '30px' },
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      params: { values: { ...descriptionBlock, firmId: FirmID }, type: 'desc' },
      form: (actions, params) => (
        <FirmAboutEditDescriptionBlockForm actions={actions} params={params} />
      ),
    }).then(onDescriptionBlockSave, response =>
      ResponseErrorDialog({ response })
    );
  };

  const addDescriptionSubBlock = () => {
    FormDialog({
      title: '',
      modeless: false,
      compact: true,
      popupStyles: { ...POPUP_STYLES.formPopupStyles, maxWidth: '540px' },
      popupInnerStyles: { ...POPUP_STYLES.formInnerStyles, marginTop: '30px' },
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      params: {
        values: { Title: '', Content: '', firmId: FirmID },
        type: 'desc-subblock',
      },
      form: (actions, params) => (
        <FirmAboutEditDescriptionBlockForm actions={actions} params={params} />
      ),
    }).then(onDescriptionSubBlockAdd, response =>
      ResponseErrorDialog({ response })
    );
  };

  const editDescriptionSubBlock = item => {
    FormDialog({
      title: '',
      modeless: false,
      compact: true,
      popupStyles: { ...POPUP_STYLES.formPopupStyles, maxWidth: '540px' },
      popupInnerStyles: { ...POPUP_STYLES.formInnerStyles, marginTop: '30px' },
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      params: { values: { ...item, firmId: FirmID }, type: 'desc-subblock' },
      form: (actions, params) => (
        <FirmAboutEditDescriptionBlockForm actions={actions} params={params} />
      ),
    }).then(onDescriptionSubBlockUpdate, response =>
      ResponseErrorDialog({ response })
    );
  };

  const editAwardsBlock = () => {
    FormDialog({
      title: '',
      modeless: false,
      compact: true,
      popupStyles: { ...POPUP_STYLES.formPopupStyles, maxWidth: '540px' },
      popupInnerStyles: { ...POPUP_STYLES.formInnerStyles, marginTop: '30px' },
      popupTitleStyles: POPUP_STYLES.formTitleStyles,
      params: { values: { ...awardsBlock, firmId: FirmID } },
      form: (actions, params) => (
        <FirmAboutEditAwardsForm actions={actions} params={params} />
      ),
    }).then(onAwardsBlockSave, response => ResponseErrorDialog({ response }));
  };

  return (
    <div className="firm-about">
      <FirmAboutDescriptionSection
        isEditMode={editMode}
        id={descriptionBlock.FirmInfoBlockID}
        title={descriptionBlock.Title}
        content={descriptionBlock.Content}
        subBlocks={descriptionSubBlocks}
        onEditBlock={editDescriptionBlock}
        onDeleteBlock={onDescriptionBlock}
        onAddDescriptionSubBlock={addDescriptionSubBlock}
        onEditSubBlock={editDescriptionSubBlock}
        onDeleteSubBlock={onDescriptionSubBlockDelete}
      />

      {editMode ? (
        <FirmAboutEditPhotos
          firmId={FirmID}
          data={{
            items: photosBlock,
            isPhotosSortable,
          }}
          actions={{
            onDelete: onPhotoDelete,
            onUpload: onPhotoUpload,
            onSorting: onPhotoGallerySorting,
            onChangeSortingMode: onPhotoGalleryChangeSortingMode,
          }}
        />
      ) : (
        !!photosBlock.length && (
          <FirmSection
            title={'Photos'}
            linkCaption={showAllPhotos ? 'Show Less' : 'View More Photos'}
            action={photosBlock.length > 5 ? viewMorePhotos : null}
          >
            <PhotoGallery
              showAll={showAllPhotos}
              items={photosBlock}
              isSortable={false}
            />
          </FirmSection>
        )
      )}

      <FirmAboutAwardsSection
        isEditMode={editMode}
        id={awardsBlock.FirmInfoBlockID}
        title={awardsBlock.Title}
        content={awardsBlock.Content}
        onEdit={editAwardsBlock}
        onDelete={onAwardsBlockDelete}
      />

      {!editMode && (
        <FirmSection title={`Work at or with ${FirmName}?`}>
          <div className="firm-about__cta">
            <div className="firm-about__logo">
              <CompanyLogo name={FirmName} logo={Logo} />
            </div>

            <div className="firm-about__buttons">
              <AddReviewButton
                text="Add Interview Review"
                link={ROUTE.ADD_REVIEW_INTERVIEW}
              />

              <AddReviewButton
                text="Add Employee Review"
                link={ROUTE.ADD_REVIEW_EMPLOYEE}
              />

              <AddReviewButton
                text="Add Company Review"
                link={ROUTE.ADD_REVIEW_COMPANY}
              />
            </div>
          </div>
        </FirmSection>
      )}
    </div>
  );
};

FirmTabAbout.propTypes = {
  isPhotosSortable: PropTypes.bool,
  editMode: PropTypes.bool.isRequired,
  firm: PropTypes.shape({
    FirmName: PropTypes.string.isRequired,
    Logo: PropTypes.string,
    FirmID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
  }),
  descriptionBlock: PropTypes.shape({
    FirmInfoBlockID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Title: PropTypes.string,
    Content: PropTypes.string,
  }),
  descriptionSubBlocks: PropTypes.array,
  awardsBlock: PropTypes.shape({
    FirmInfoBlockID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Title: PropTypes.string,
    Content: PropTypes.string,
  }),
  photosBlock: PropTypes.array,
  onPhotoGallerySorting: PropTypes.func,
  onPhotoGalleryChangeSortingMode: PropTypes.func,
  onPhotoDelete: PropTypes.func,
  onPhotoUpload: PropTypes.func,
  onDescriptionBlockSave: PropTypes.func,
  onDescriptionBlock: PropTypes.func,
  onDescriptionSubBlockAdd: PropTypes.func,
  onDescriptionSubBlockUpdate: PropTypes.func,
  onDescriptionSubBlockDelete: PropTypes.func,
  onAwardsBlockSave: PropTypes.func,
  onAwardsBlockDelete: PropTypes.func,
};

export default FirmTabAbout;
